import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Button } from "react-md";
import {
  getUser,
  sendPasswordResetLink,
  getAllRoles,
  getAllMajorCompanies,
  updateUser,
  disableUser,
} from "actions/user";
import MCard from "components/Shared/MCard.tsx";
import { DATA_TYPE_BOOLEAN, DATA_TYPE_DROPDOWN2 } from "typeConstants/dataTypes";
import { dateTimeToStringWithNumOfDays } from "components/Shared/Formatters";
import { MSpinner } from "components/Shared";
import responseHandler from "responseHandler";
import * as yup from "yup";
import { MinRequiredPasswordLength, PasswordRegex } from "typeConstants/preferenceTypes";
import webConfig from "middlewares/WebConfig";
import { userHasRight } from "selectors/rights";
import { AUTHENTICATION_EDIT } from "typeConstants/rights";

class UserInformation extends React.Component {
  constructor(props) {
    super(props);
    this.handlePasswordSend = this.handlePasswordSend.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.forcedPassword = null;
    this.masqueradeForm = React.createRef();
    this.handleMasqueradeSubmit = this.handleMasqueradeSubmit.bind(this);
    this.handleDisableUser = this.handleDisableUser.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!this.props.updatingUserInfo && prevProps.updatingUserInfo) this.props.getUser(this.props.internetUserId);
  }

  UNSAFE_componentWillMount() {
    if (this.props.canEditUser) {
      if (this.props.allRoles.length === 0) {
        this.props.getAllRoles();
      }
      if (this.props.allMajorCompanies.length === 0) {
        this.props.getAllMajorCompanies();
      }
    }
  }

  handlePasswordSend() {
    this.props.sendPasswordResetLink(this.props.userInformation.username);
  }

  handleDisableUser() {
    this.props.disableUser(this.props.internetUserId,
      () => {
        this.props.getUser(this.props.internetUserId);
      },
      null);
  }

  handleSave(editedData, editedObj) {
    let user = this.props.userInformation;

    this.props.updateUser(this.props.internetUserId, {
      username: user.username,
      company: user.companyName,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roleName: user.roleName,
      isDisabled: user.isDisabled,
      mustChangePassword: user.mustChangePassword,
      receivesEmailConfirmations: user.receivesEmailConfirmations,
      ...editedObj
    });
  }

  testPasswordComplexity(password) {
    let counter = 0;
    PasswordRegex.forEach(regex => {
      if (regex.test(password)) {
        counter++;
      }
    });
    if (counter < 3) {
      return false;
    }
    return true;
  }

  handleMasqueradeSubmit() {
    this.masqueradeForm.current.submit();
  }

  render() {
    if (this.props.isFetchingAllRolesList || !this.props.allRoles || this.props.isFetchingAllMajorCompanies || !this.props.allMajorCompanies || this.props.updatingUserInfo) {
      return <MSpinner />;
    }
    const { userInformation, ssoToken, canEditUsername } = this.props;

    const passwordChangedLastDate = !userInformation.passwordChangedLastDate
      ? ""
      : dateTimeToStringWithNumOfDays(userInformation.passwordChangedLastDate);
    let colDefs = [
      { key: "internetUserId", label: "ID", value: this.props.internetUserId, editable: false },
      { key: "username", label: "Username", value: userInformation.username, editable: canEditUsername },
      {
        key: "company",
        label: "Company",
        value: { value: userInformation.companyName, label: userInformation.companyName },
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: this.props.allMajorCompanies, nullable: false }
      },
      { key: "firstName", label: "First Name", value: userInformation.firstName },
      { key: "lastName", label: "Last Name", value: userInformation.lastName },
      { key: "email", label: "Email", value: userInformation.email, validation: yup.string().nullable().email("Must be a valid email").required("Email is required") },
      {
        key: "receivesEmailConfirmations",
        label: "Receives Email Confirmations",
        value: userInformation.receivesEmailConfirmations,
        dataType: DATA_TYPE_BOOLEAN,
      },
      {
        key: "roleName",
        label: "Role",
        value: { value: userInformation.roleName, label: userInformation.roleName },
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: this.props.allRoles, nullable: false },
        validation: yup.string().nullable().required("Role is required")
      },
      {
        key: "isDisabled",
        label: "Disabled",
        value: userInformation.isDisabled,
        dataType: DATA_TYPE_BOOLEAN,
        conditionalClasses: (fld) => { return fld.value ? ["highlight"] : []; },
      },
      {
        key: "isLockedOut",
        label: "Locked Out",
        value: userInformation.isLockedOut,
        editable: userInformation.isLockedOut,
        dataType: DATA_TYPE_BOOLEAN,
      },
      {
        key: "mustChangePassword",
        label: "Must Change Password",
        value: userInformation.mustChangePassword,
        dataType: DATA_TYPE_BOOLEAN
      },
      {
        key: "passwordChangedOn",
        label: "Password Changed On",
        value: passwordChangedLastDate,
        editable: false
      },
      {
        key: "forcedPassword",
        label: "Set Password",
        value: this.forcedPassword,
        validation: yup.string().nullable().min(MinRequiredPasswordLength).test({
          name: "Password Complexity",
          test: (value) => this.testPasswordComplexity(value),
          message: "Password should have atleast one uppercase, one lowercase, one digit or a special character"
        })
      },
    ];

    colDefs.unshift({
      key: "editButton",
      control: <>
        <Button
          style={{ marginBottom: 10 }}
          primary
          raised
          onClick={this.handlePasswordSend}>
          Send Password Reset Link
        </Button>
        {userInformation.isDisabled ||
          (
            <Button
              style={{ marginBottom: 10, marginLeft: 10 }}
              primary
              raised
              onClick={this.handleDisableUser}>
              Disable User
            </Button>
          )}
        {!this.props.canEditUser || userInformation.isDisabled ||
          (
            <form
              ref={this.masqueradeForm}
              style={{ display: "inline" }}
              action={`${webConfig.webApiRoot}/account/login/masquerade`}
              method="post"
              target="_blank">
              <input type="hidden" name="username" value={userInformation.username} />
              <input type="hidden" name="sso" value={ssoToken} />
              <Button
                style={{ marginBottom: 10, marginLeft: 10 }}
                primary
                raised
                onClick={this.handleMasqueradeSubmit}>
                Log in as User
              </Button>
            </form>
          )}
      </>
    });

    return (
      <MCard
        title="User Information"
        isEditable={this.props.canEditUser}
        fields={colDefs}
        onEditFinished={this.handleSave}
      />
    );
  }
}

UserInformation.propTypes = {
  userInformation: PropTypes.object.isRequired,
  internetUserId: PropTypes.number.isRequired,
  canEditUser: PropTypes.bool,

  //connected props
  getUser: PropTypes.func.isRequired,
  sendPasswordResetLink: PropTypes.func,
  isFetchingAllRolesList: PropTypes.bool.isRequired,
  getAllRoles: PropTypes.func.isRequired,
  allRoles: PropTypes.array.isRequired,
  isFetchingAllMajorCompanies: PropTypes.bool.isRequired,
  allMajorCompanies: PropTypes.array.isRequired,
  getAllMajorCompanies: PropTypes.func.isRequired,
  makeToast: PropTypes.func.isRequired,
  updatingUserInfo: PropTypes.bool.isRequired,
  updateUser: PropTypes.func.isRequired,
  ssoToken: PropTypes.string.isRequired,
  disableUser: PropTypes.func.isRequired,
  canEditUsername: PropTypes.bool.isRequired,
};

const mapStateToProps = state => {
  return {
    isFetchingInternetUser: state.user.isFetchingInternetUser,
    isFetchingAllRolesList: state.user.isFetchingAllRolesList,
    allRoles: state.user.allRoles,
    isFetchingAllMajorCompanies: state.user.isFetchingAllMajorCompanies,
    allMajorCompanies: state.user.allMajorCompanies,
    updatingUserInfo: state.user.updatingUserInfo,
    ssoToken: state.user.profile.token,
    canEditUsername: userHasRight(AUTHENTICATION_EDIT)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  getUser(id) {
    dispatch(getUser(id));
  },
  sendPasswordResetLink(username) {
    dispatch(sendPasswordResetLink(username));
  },
  getAllRoles() {
    dispatch(getAllRoles());
  },
  getAllMajorCompanies() {
    dispatch(getAllMajorCompanies());
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  updateUser(internetUserId, dto) {
    dispatch(updateUser(internetUserId, dto));
  },
  disableUser(internetUserId) {
    dispatch(disableUser(internetUserId));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserInformation);
